import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { LegalEntityDetails } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, startWith, switchMap } from 'rxjs';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { RepresentativesTabComponent } from './representatives-tab/representatives-tab.component';

type Tab = 'details' | 'representatives';
interface TabDef {
  key: Tab;
  label: string;
}

@Component({
  selector: 'app-investor-details',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    DetailsPageHeaderComponent,
    DetailsTabComponent,
    RepresentativesTabComponent,
    ButtonComponent,
  ],
  templateUrl: './investor-details.component.html',
  styleUrl: './investor-details.component.scss',
})
export class InvestorDetailsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly investorService = inject(LegalEntityService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  investor = signal<LegalEntityDetails | null>(null);
  isLoading = signal(true);
  isSaving = signal(false);
  isCancelling = signal(false);
  error = signal<string | null>(null);
  isToggling = false;
  isDeleting = false;

  activeTab = signal<Tab>('details');
  tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: 'admin.investors.tabs.details' },
    {
      key: 'representatives',
      label: 'admin.investors.tabs.representatives',
    },
  ];

  private investorIdSig = toSignal(
    this.route.paramMap.pipe(startWith(this.route.snapshot.paramMap)),
    { requireSync: true },
  ).bind(this) as () => URLSearchParams | any;

  investorId = computed(() => {
    const pm = this.investorIdSig?.();
    return pm?.get?.('legalEntityId') ?? pm?.get?.('id') ?? '';
  });

  @ViewChild(DetailsTabComponent) detailsTab?: DetailsTabComponent;

  constructor() {
    effect(() => {
      const id = this.investorId();
      if (!id) {
        this.isLoading.set(false);
        this.error.set('Missing id');
        return;
      }
      this.loadEntity(id);
    });
  }

  private loadEntity(id: string): void {
    this.isLoading.set(true);
    this.investorService
      .getLegalEntityById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (entity) => {
          this.investor.set(entity);
          this.error.set(null);
        },
        error: (err) => {
          this.error.set(err?.message ?? 'Failed to load');
          this.investor.set(null);
        },
      });
  }

  goBack(): void {
    const target = history.state?.from || '/admin/investors';
    this.router.navigate([target], { relativeTo: this.route });
  }

  setTab(tab: Tab): void {
    this.activeTab.set(tab);
  }

  onSaveDetails(): void {
    const save$ = this.detailsTab?.save();
    if (!save$) return;

    this.isSaving.set(true);
    save$
      .pipe(
        switchMap(() =>
          this.investorService.getLegalEntityById(this.investorId()),
        ),
        finalize(() => this.isSaving.set(false)),
      )
      .subscribe({
        next: (fresh) =>
          this.investor.set(fresh as unknown as LegalEntityDetails),
        error: () => {
          this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
        },
      });
  }

  onCancelDetails(): void {
    this.detailsTab?.cancel();
  }

  private getSuspendedFlag(entity: LegalEntityDetails | null): boolean {
    if (entity?.suspended !== undefined) return !!entity.suspended;
    return false;
  }

  get isSuspended(): boolean {
    return this.getSuspendedFlag(this.investor());
  }

  get isDeactivated(): boolean {
    return this.isSuspended;
  }

  onToggleActivation(): void {
    const id = this.investorId();
    if (!id || this.isToggling) return;

    this.isToggling = true;
    const currentlySuspended = this.isSuspended;

    const request$ = currentlySuspended
      ? this.investorService.deSuspendLegalEntity(id)
      : this.investorService.suspendLegalEntity(id);

    request$
      .pipe(
        switchMap(() => this.investorService.getLegalEntityById(id)),
        finalize(() => (this.isToggling = false)),
      )
      .subscribe({
        next: (entity) => {
          this.investor.set(entity);
          const okKey = currentlySuspended
            ? 'admin.toasts.entityDeSuspended'
            : 'admin.toasts.entitySuspended';
          this.toast.success(this.translate.instant(okKey));
        },
        error: () => {
          const errKey = currentlySuspended
            ? 'admin.toasts.entityDeSuspendFailed'
            : 'admin.toasts.entitySuspendFailed';
          this.toast.error(this.translate.instant(errKey));
        },
      });
  }

  onDelete(): void {
    const id = this.investorId();
    if (!id || this.isDeleting) return;

    this.isDeleting = true;
    this.investorService
      .deleteLegalEntity(id)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.toast.success(this.translate.instant('admin.toasts.deleted'));
          this.goBack();
        },
        error: () =>
          this.toast.error(this.translate.instant('admin.toasts.deleteFailed')),
      });
  }
}
